<template>
    <div
        class="form-group"
        :class="computedClasses"
    >
        <div
            class=""
            :class="{ 'input-group': grpPrependSlot || grpAppendSlot }"
        >
            <div
                class="input-group-prepend"
                v-if="grpPrependSlot"
            >
                <slot name="grpPrepend"></slot>
            </div>
            <v-select
                :key="id"
                :taggable="allowCreate"
                :pushTags="pushTags"
                class="city-select"
                :class="[inputClasses, { 'vs--selected': hasValue, nowrap: noWrap }]"
                :name="name"
                :inputId="id"
                :label="label"
                :autocomplete="autocomplete"
                :placeholder="placeholder"
                :filterable="_filterable"
                :options="optionsList"
                :clearable="clearable"
                :searchable="searchable"
                :multiple="multiple"
                :reduce="reduceItem"
                :noDrop="noDrop"
                :disabled="disabled"
                :resetOnOptionsChange="resetOnOptionsChange"
                :appendToBody="appendToBody"
                :createOption="createOption"
                v-model="valueCopy"
                :loading="loadingSpinner"
                @input="handleModelInput"
                @option:selected="onSelected"
                @search:focus="handleFocus(true)"
                @search:blur="handleFocus(false)"
                @search="onSearch"
                @open="onOpen"
                @close="onClose"
                @option:created="optionCreated"
                @option:selecting="optionSelecting"
            >
                <template
                    #search="{ attributes, events }"
                    v-if="!multiple"
                >
                    <input
                        class="vs__search"
                        v-bind="getInputSearchLabel(attributes)"
                        v-on="events"
                        @keydown="onInputSerachHandleKeydown"
                        @blur="onInputSearchBlur"
                    />
                </template>
                <template v-slot:selected-option="option">
                    <slot
                        name="selected-option"
                        :option="initialData && valueCopy && reduce(initialData) === valueCopy ? initialData : option"
                        :label="label"
                        ><!-- computed label reativo-->
                        {{ getOptionLabel(option) }}
                    </slot>
                </template>
                <template v-slot:option="option">
                    <slot
                        name="option"
                        :option="option"
                        :label="label"
                    >
                        <div v-html="getOptionHtml(option)"></div>
                    </slot>
                </template>
                <template #list-footer>
                    <li
                        v-show="hasNextPage && infiniteScroll"
                        :id="`loadMore-${uniqueId}`"
                        class="loader"
                    >
                        A carregar mais opções...
                    </li>
                </template>
                <template v-slot:no-options="{ search, searching }">
                    <template v-if="searching"> Nenhum resultado encontrado </template>
                    <em
                        v-else
                        style="opacity: 0.5"
                        >Comece a escrever para pesquisar</em
                    >
                </template>
            </v-select>

            <div
                class="input-group-append"
                :class="grpAppendClass"
                v-if="grpAppendSlot"
            >
                <slot name="grpAppend"></slot>
            </div>

            <label
                class="material-label"
                :class="activeClass"
                :for="id"
            >
                <slot></slot>
            </label>
            <small
                v-if="error"
                class="invalid-feedback"
                >{{ error }}</small
            >
        </div>
        <HelpSpan :helpText="helpText" />
    </div>
</template>

<script>
import { debounce, formatUrl, getConfigProp, resolvePath, strippedHTML } from "@config/utils";

export default {
    name: "material-select",
    components: {
        HelpSpan: () => import(/* webpackChunkName: "help-span" */ "@/components/form/HelpSpan.vue")
    },
    data() {
        return {
            valueCopy: null,
            focus: false,
            valid: true,
            treeData: [],
            optionsAjax: [],
            loadingSpinner: false,
            search: "",
            scrollListener: null,
            limit: this.paginationlimit,
            uniqueId: Math.random().toString(36).substr(2, 9),
            newOptionLabel: null,
            optionSeleted: null,
            inputSercHandlerClear: false
        };
    },
    computed: {
        getInputSearchLabel() {
            return option => {
                //nao funciona quando for multiple select
                if (!this.multiple && option?.value == "" && this.valueCopy && this.focus && !this.inputSercHandlerClear) {
                    option.value = this.getOptionLabel(this.optionSeleted);
                }

                return option;
            };
        },
        // computed label reativo
        getOptionLabel() {
            return option => {
                const lastLabel = this.getLabelFrom(this.optionSeleted);
                if (lastLabel && !this.multiple) {
                    return this.clearOptionLabel(lastLabel);
                }
                const initialLabel = this.getLabelFrom(this.initialData);
                if (initialLabel) {
                    if (!this.multiple && initialLabel && !(JSON.parse(JSON.stringify(this.optionSeleted)) === JSON.parse(JSON.stringify(option)))) this.optionSeleted = this.initialData;
                    return this.clearOptionLabel(initialLabel);
                }

                if (typeof option === "object") {
                    // if (!option.hasOwnProperty(this.label)) {
                    //     return console.warn(
                    //         `[vue-select warn]: Label key "option.${this.label}" does not` +
                    //             ` exist in options object ${JSON.stringify(option)}.\n` +
                    //             "https://vue-select.org/api/props.html#getoptionlabel"
                    //     );
                    // }

                    const res = resolvePath(option, this.label);
                    if (!this.multiple && res && !(JSON.stringify(this.optionSeleted) === JSON.stringify(option))) this.optionSeleted = option;
                    return this.clearOptionLabel(res);
                }
                return this.clearOptionLabel(option);
            };
        },

        grpPrependSlot() {
            if (this.$scopedSlots) {
                return this.$scopedSlots.grpPrepend;
            }
            return null;
        },
        grpAppendSlot() {
            if (this.$scopedSlots) {
                return this.$scopedSlots.grpAppend;
            }
            return null;
        },
        optionsList() {
            if (this.infiniteScroll) return this.paginated;
            if (this.ajaxOptions?.url) return this.optionsAjax;

            if (this.indentText) return this.hasChild(this.options);

            return this.options;
        },
        computedErrors() {
            return typeof this.errorMessages === "string" ? [this.errorMessages] : this.errorMessages;
        },
        computedClasses() {
            return {
                "material--active": this.focus,
                "material--disabled": this.disabled,
                "material--has-errors": Boolean(!this.valid || (this.errorMessages && this.errorMessages.length)),
                "material--raised": Boolean(
                    this.focus ||
                        this.hasValue || // has value
                        (this.placeholder && !this.hasValue)
                ), // has placeholder
                iconPlace: this.iconPlace
            };
        },
        activeClass() {
            return {
                active: Boolean(
                    this.focus ||
                        this.hasValue || // has value
                        (this.placeholder && !this.hasValue) ||
                        (this.allowCreate && this.newOptionLabel)
                ) // has placeholder
            };
        },
        hasValue() {
            return this.multiple ? this.valueCopy != null && this.valueCopy.length > 0 : !this.idNull;
        },
        idNull() {
            return this.valueCopy === undefined || this.valueCopy === null;
        },
        reduceItem() {
            if (this.reduceObject) {
                return option => option;
            }
            return this.reduce;
        },

        filtered() {
            let options = this.ajaxOptions?.url ? this.optionsAjax : this.options;
            if (!Array.isArray(options)) return [];
            return options.filter(option => (this.search && this.label in option ? (option[this.label] ?? "").toString().includes(this.search) : true));
        },
        paginated() {
            return this.filtered.slice(0, this.limit);
        },
        hasNextPage() {
            return this.paginated.length < this.filtered.length;
        },
        _filterable() {
            return this.filterable || !this.ajaxOptions || this.ajaxOptions?.shouldLoadDataOnce === true;
        }
    },
    beforeMount() {
        this.copyValue(this.value);
    },
    mounted() {
        //   this.observer = new IntersectionObserver(this.infiniteScroll);
    },
    methods: {
        hasChild(options) {
            let leftover = [];
            options.forEach(item => {
                if (this.indentationTarget) {
                    if (!this.indentationTarget || (this.indentationTarget in item && item[this.indentationTarget] == null)) {
                        this.treeData.push({ ...item, ...{ level: 1 } });
                    } else {
                        let parent = this.indentationTarget in item ? this.searchParent(item[this.indentationTarget]) : null;
                        if (!parent) {
                            leftover.push(item);
                        } else {
                            this.addLeftSpace(parent, item);
                        }
                    }
                }
            });

            if (leftover.length > 0) this.hasChild(leftover);

            return this.treeData;
        },
        searchParent(parent_id) {
            return this.treeData.find(item => {
                return item.id === parent_id;
            });
        },
        addLeftSpace(parent, item) {
            let level = parent.level + 1;
            item[this.label] = this.indentTextSymbol.repeat(2 * level) + item[this.label];
            let position = this.treeData.indexOf(parent);
            this.treeData.splice(position + 1, 0, { ...item, ...{ level } });
        },
        handleModelInput(value) {
            if (value == null) {
                this.$emit("cleared", value);
            }
            this.$emit("input", value);
            this.handleValidation();
        },
        handleFocus(focused) {
            this.focus = focused;
        },
        handleValidation() {
            this.valid = true; //this.$el ? this.$el.querySelector('.city-select').validity.valid : this.valid
        },
        copyValue(value) {
            // let _val = value;
            // if(this.multiple){
            //   _val = _val.map( option => this.reduceItem(option));
            // }else{
            //   _val = this.reduceItem(_val);
            // }
            this.valueCopy = value;
            this.handleValidation();
        },
        clearOptionLabel(option) {
            if (typeof option === "string") {
                // Substitui &nbsp; por espaço normal e remove espaços invisíveis Unicode
                option = option.replace(/&nbsp;/g, " ").replace(/[\u00A0\u200B\u200C\u200D]/g, "");

                // Remove indentação inicial com símbolos definidos em this.indentTextSymbol
                const escapedSymbols = this.indentTextSymbol.replace(/[-[\]/{}()*+?.\\^$|]/g, "\\$&");
                const regex = new RegExp(`^([${escapedSymbols}]\\s*)+`);

                option = option.replace(regex, "");
            }
            return option;
        },

        getOptionHtml(option) {
            if (this.allowCreate && option?.createdOptionLabel) return option.createdOptionLabel;
            if (this.funcItemHtml) {
                return this.funcItemHtml(option);
            } else if (typeof option === "object") {
                return resolvePath(option, this.label);
            } else {
                return "";
            }
        },
        // getOptionLabel(option) {
        //     if (this.initialData && this.valueCopy && this.reduce(this.initialData) == this.valueCopy) {
        //         if (this.initialData[this.label]) return strippedHTML(this.initialData[this.label])?.trim();
        //     }
        //     if (typeof option === "object") {
        //         return strippedHTML(resolvePath(option, this.label))?.trim();
        //     }
        //     return option;
        // },
        getQueryParams(params = {}) {
            // Substitui 'replceSeachQuery' pelos params.q em todos os queryParams,
            let ajaxOptions = JSON.parse(JSON.stringify(this.ajaxOptions));

            if (params?.q && ajaxOptions?.queryParams) {
                let found = false,
                    q = this.search; // params.q;
                for (const k in ajaxOptions.queryParams) {
                    const v = ajaxOptions.queryParams[k];
                    if (typeof v === "string" && v.includes("replceSeachQuery")) {
                        ajaxOptions.queryParams[k] = v.replace(/replceSeachQuery/g, q);
                        found = true;
                    }
                }
                if (found) delete params.q;
            }

            return {
                params: Object.fromEntries(
                    new URLSearchParams(
                        Object.fromEntries(
                            new URLSearchParams({
                                ...(ajaxOptions?.queryParams ?? {}),
                                ...(ajaxOptions?.limit ? { limit: ajaxOptions?.limit } : {}),
                                ...params
                            })
                        )
                    )
                ),
                responseType: "json"
            };
        },

        onOpen() {
            //cal Ajax on dropdonw open
            //cal Ajax only one time  this.ajaxOptions?.shouldLoadDataOnce &&
            if (this.ajaxOptions?.shouldLoadDataOnDropdownOpen && this.optionsAjax.length == 0) this.performSearch();
            this.$nextTick(() => {
                if (this.infiniteScroll) this.addScrollListener();
            });
        },
        onClose() {
            this.removeScrollListener();
        },

        dbSearch: function (search) {
            const timeout = this.debounceTimeout;
            debounce((search, vm) => {
                vm.performSearch(search);
            }, timeout)(search, this);
        },

        onSearch(search) {
            this.search = search;
            if (!this.ajaxOptions) return;
            //cal Ajax only one time
            if (this.ajaxOptions?.shouldLoadDataOnce && this.optionsAjax.length > 0) return;
            if (!search.length) return;
            //para Remover e cria novo UL Li list
            this.removeScrollListener();
            this.dbSearch(search, this);
            this.addScrollListener();
        },

        performSearch(search = null) {
            this.loadingSpinner = true;
            const url = formatUrl(this.ajaxOptions.url, {
                id: this.ajaxOptions?.urlParams?.id,
                model: this.ajaxOptions?.urlParams?.model
            });

            const queryParams = this.getQueryParams(search && this.searchable && !this._filterable ? { q: encodeURIComponent(search) } : {});
            this.$http
                .get(url, queryParams)
                .then(response => {
                    this.optionsAjax = Array.isArray(response?.data?.data) ? response.data.data : Array.isArray(response?.data) ? response.data : [];

                    this.loadingSpinner = false;
                })
                .catch(error => {
                    console.error("Erro:", error);
                    this.loadingSpinner = false;
                });
        },

        // pagination
        addScrollListener() {
            const dropdown = document.querySelector(`#loadMore-${this.uniqueId}`)?.closest("ul") || null;
            if (dropdown) {
                this.scrollListener = this.handleScroll;
                dropdown.addEventListener("scroll", this.scrollListener);
            }
        },
        removeScrollListener() {
            const dropdown = document.querySelector(`#loadMore-${this.uniqueId}`)?.closest("ul") || null;
            if (!dropdown) return;
            if (this.scrollListener) dropdown.removeEventListener("scroll", this.scrollListener);
            dropdown.remove();
        },
        handleScroll(event) {
            const { target } = event;
            const currentScrollTop = target.scrollTop;
            if (target.scrollTop + target.clientHeight >= target.scrollHeight - 10) {
                if (this.hasNextPage) {
                    this.limit += this.paginationlimit;
                    this.$nextTick(() => {
                        target.scrollTop = currentScrollTop;
                    });
                }
            }
        },
        onSelected(value) {
            this.$emit("selected", value);
        },

        getOptionSeleted(id) {
            let item = null;
            if (this.optionsList && Array.isArray(this.optionsList) && this.optionsList.length > 0) {
                item = this.optionsList.find(data => this.reduce(data) == id);
            }
            if (!item && this.initialData && id && this.reduce(this.initialData) == id) {
                item = this.initialData;
            }
            return item;
        },

        optionCreated(option) {},

        optionSelecting(option) {
            if (option && typeof option === "object" && "createdOptionLabel" in option) {
                delete option.createdOptionLabel;
                this.newOptionLabel = option[this.label];
                this.$emit("created", option);
            } else this.newOptionLabel = null;
        },
        createOption(searchText) {
            return { [this.label]: searchText, createdOptionLabel: this.templateCreate(searchText) };
        },
        getLabelFrom(item) {
            if (item && (this.valueCopy || this.valueCopy === 0) && this.reduce(item) == this.valueCopy) {
                return item[this.label] ?? null;
            }
            return null;
        },
        onInputSerachHandleKeydown(e) {
            if (this.inputSercHandlerClear && e.target.value == "") return;
            this.inputSercHandlerClear = e.key === "Delete" || e.key === "Backspace";
        },
        onInputSearchBlur() {
            this.inputSercHandlerClear = false;
        }
    },
    watch: {
        search: {
            handler: function (newValue) {
                if (newValue || !this.value) this.$emit("update:inputValue", newValue);
            }
        },
        value: {
            deep: true, // "deep" por causa do multiple que é um array
            handler: function (newValue, oldValue) {
                this.copyValue(newValue);
                if (newValue != oldValue) {
                    this.inputSercHandlerClear = false;
                    this.$emit("changed", this.getOptionSeleted(newValue));
                    if (!newValue) this.$emit("item-unselected", null);
                }
            }
        },
        hasNextPage: {
            handler: function (newValue) {
                this.$nextTick(() => {
                    if (this.infiniteScroll && newValue == true) this.addScrollListener();
                });
            }
        },
        ajaxOptions: {
            deep: true,
            handler: function (newValue, oldValue) {
                const newQueryParams = newValue?.queryParams || {};
                const oldQueryParams = oldValue.queryParams || {};
                if (!oldQueryParams || Object.values(oldQueryParams).every(value => value === undefined) || JSON.stringify(newQueryParams) === JSON.stringify(oldQueryParams)) return;
                this.optionsAjax = [];
                this.valueCopy = null;
            }
        }
    },
    props: {
        id: {
            type: String,
            default() {
                return "input-" + Math.random().toString(36).substr(2, 9);
            }
        },
        name: {
            type: String,
            default: null
        },
        value: {
            default: null
        },
        options: {
            type: [Array],
            default() {
                return [];
            }
        },
        label: {
            type: String,
            default: "label"
        },
        placeholder: {
            type: String,
            default: ""
        },
        disabled: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: true
        },
        searchable: {
            type: Boolean,
            default: true
        },
        filterable: {
            type: Boolean,
            default: false
        },
        multiple: {
            type: Boolean,
            default: false
        },
        reduce: {
            type: Function,
            default: option => option.id
        },
        reduceObject: {
            type: Boolean,
            default: false
        },
        noDrop: {
            type: Boolean,
            default: false
        },

        resetOnOptionsChange: {
            type: [Boolean, Function],
            default: false
        },
        required: {
            type: Boolean,
            default: false
        },
        autocomplete: {
            type: String,
            default: "off"
        },
        errorMessages: {
            type: [Array, String],
            default: null
        },
        inputClasses: {
            type: [Array, Object, String],
            default: null
        },
        noWrap: {
            type: Boolean,
            default: true
        },
        error: {
            type: String,
            default: null
        },
        helpText: {
            type: String,
            default: null
        },
        indentText: {
            type: Boolean,
            default: false
        },
        indentationTarget: {
            type: String,
            default: null
        },
        appendToBody: {
            type: Boolean,
            default: true
        },
        ajaxOptions: {
            type: [Object],
            default: null
            /*
             default: {
             url
             limit
             shouldLoadDataOnce
             shouldLoadDataOnDropdownOpen
             queryParams
             urlParams{
                 id,
                 model,
                 }
             }
             */
        },
        debounceTimeout: {
            type: Number,
            default: () => {
                return getConfigProp("search")?.wait ?? 1000;
            }
        },
        infiniteScroll: {
            type: Boolean,
            default: false
        },
        paginationlimit: {
            type: Number,
            default: 12
        },
        initialData: {
            type: [Array, Object, String],
            default: null
        },
        inputValue: {
            type: String,
            default: null
        },
        iconPlace: {
            type: Boolean,
            default: false
        },
        grpAppendClass: {
            type: String,
            default: ""
        },
        funcItemHtml: {
            type: Function,
            default: null
        },

        pushTags: {
            type: Boolean,
            default: false
        },

        templateCreate: {
            type: Function,
            default: label => label
        },
        allowCreate: {
            type: Boolean,
            default: false
        },
        indentTextSymbol: {
            type: String,
            default: "\xa0" //espaço ' '
        }
    }
};
</script>

<style scoped lang="scss">
.city-form-group .city-select.is-invalid {
    padding: 0;
}
</style>
<style lang="scss">
.iconPlace .vs__actions {
    position: relative;
    &:after {
        font-size: 22px;
        font-family: "Font Awesome 6 Free";
        content: "\f3c5";
        position: absolute;
        top: -11px;
        left: -24px;
        color: #9cd5ff;
        font-weight: 600;
    }
}

ul.vs__dropdown-menu {
    border: unset;
}
ul.vs__dropdown-menu li {
    white-space: normal;
    position: relative;
    &:not(:nth-last-child(2))::after {
        content: "";
        position: absolute;
        bottom: -1px;
        left: 0;
        width: 100%;
        height: 1px;
        background-color: var(--body-color);
        opacity: 0.2;
    }
    p {
        margin-top: 0;
        margin-bottom: 0.3rem;
    }
}

.vs__spinner {
    margin: 0;
    padding: 0;
    margin-top: -9px;
}
.city-form-group.city-bg-sm .vs__spinner {
    margin-top: -2px;
    height: 3.5em;
    width: 3.5em;
}

.city-form-group .input-group .input-group-append .v-select.city-select {
    input {
        width: 100%;
    }
}
</style>
